import React from 'react';
import { VaAlert } from '@department-of-veterans-affairs/component-library/dist/react-bindings';

import {
  currentOrPastDateSchema,
  currentOrPastDateUI,
  titleUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import VaDateField from 'platform/forms-system/src/js/web-component-fields/VaDateField';
import { wrapDateUiWithDl } from '../helpers/reviewHelpers';
import { validateDateRange } from '../helpers/dateRangeCustom';
import { validateNotOnOrBeforeDOB } from '../helpers/dobValidation';

const shouldUseEmploymentDateValidation = formData =>
  !!formData?.form218940EmployerDateValidation;

const addDateRangeError = ({
  errors,
  fromDate,
  toDate,
  formData,
  errorMessage,
}) => {
  if (!errors || !fromDate || !toDate) {
    return;
  }

  validateDateRange(
    { to: errors },
    { from: fromDate, to: toDate },
    formData,
    undefined,
    {
      pattern: errorMessage,
    },
    true,
  );
};

const validateDisabilityDateNotBeforeDOB = validateNotOnOrBeforeDOB(
  "Enter a valid date. The date your disability affected full-time employment cannot be on or before the Veteran's date of birth.",
);
const validateLastWorkedDateNotBeforeDOB = validateNotOnOrBeforeDOB(
  "Enter a valid date. The date you last worked full-time cannot be on or before the Veteran's date of birth.",
);
const validateDisabledWorkDateNotBeforeDOB = validateNotOnOrBeforeDOB(
  "Enter a valid date. The date you became too disabled to work cannot be on or before the Veteran's date of birth.",
);

const validateLastWorkedDate = (errors, date, formData = {}) => {
  if (!shouldUseEmploymentDateValidation(formData)) {
    return;
  }

  addDateRangeError({
    errors,
    fromDate: formData?.disabilityDate,
    toDate: date,
    formData,
    errorMessage:
      'Date you last worked full-time must be on or after the date your disability affected full-time employment',
  });
};

const validateDisabledWorkDate = (errors, date, formData = {}) => {
  if (!shouldUseEmploymentDateValidation(formData)) {
    return;
  }

  addDateRangeError({
    errors,
    fromDate: formData?.lastWorkedDate,
    toDate: date,
    formData,
    errorMessage:
      'Date you became too disabled to work must be on or after the date you last worked full-time',
  });
};

const dateUI = (options, validations = []) => {
  const ui = currentOrPastDateUI(options);

  return wrapDateUiWithDl({
    ...ui,
    'ui:webComponentField': VaDateField,
    'ui:validations': [...(ui['ui:validations'] || []), ...validations],
  });
};

/** @type {PageSchema} */
export default {
  uiSchema: {
    ...titleUI('Section III: Employment statement'),
    'ui:description': (
      <div>
        <VaAlert status="info" class="vads-u-margin-top--3" uswds visible>
          <h2 slot="headline">
            <b>What to expect:</b>
          </h2>
          <div className="vads-u-margin--0">
            <ul style={{ marginBottom: 0 }}>
              <li>When your disability began affecting your work</li>
              <li>Date you last worked full-time</li>
              <li>
                Employment details for the last 5 years you worked (employer
                names, addresses, dates, job duties)
              </li>
              <li>Your highest annual earnings and what year</li>
              <li>Current income information (if working)</li>
              <li>Whether you've tried to find work since becoming disabled</li>

              <li>Takes about 20-25 minutes (longest section)</li>
            </ul>
          </div>
        </VaAlert>
        <div className="vads-u-margin-top--5">
          <h2 className="vads-u-font-size--h3" style={{ marginTop: 0 }}>
            Effects of your disability on employment
          </h2>
          <p>
            Enter the dates your service-connected disability affected your
            ability to work.
          </p>
        </div>
      </div>
    ),
    disabilityDate: dateUI(
      {
        title: 'Date your disability affected full-time employment',
        hint: 'For example: January 19 2022',
        errorMessages: {
          required:
            'Enter the complete date your disability affected your ability to work full-time',
          pattern:
            'Enter the complete date your disability affected your ability to work full-time',
        },
      },
      [validateDisabilityDateNotBeforeDOB],
    ),

    lastWorkedDate: dateUI(
      {
        title: 'Date you last worked full-time',
        hint: 'For example: January 19 2022',
        errorMessages: {
          required: 'Enter the complete date you last worked full-time',
          pattern: 'Enter the complete date you last worked full-time',
        },
      },
      [validateLastWorkedDateNotBeforeDOB, validateLastWorkedDate],
    ),
    disabledWorkDate: dateUI(
      {
        title: 'Date you became too disabled to work',
        hint: 'For example: January 19 2022',
        errorMessages: {
          required: 'Enter the complete date you became too disabled to work',
          pattern: 'Enter the complete date you became too disabled to work',
        },
      },
      [validateDisabledWorkDateNotBeforeDOB, validateDisabledWorkDate],
    ),
  },
  schema: {
    type: 'object',
    properties: {
      disabilityDate: currentOrPastDateSchema,
      lastWorkedDate: currentOrPastDateSchema,
      disabledWorkDate: currentOrPastDateSchema,
    },
    required: ['disabilityDate', 'lastWorkedDate', 'disabledWorkDate'],
  },
};
